import { cn } from '@/lib/utils'
import { NavLink, type NavLinkProps, type To, useLocation, useOutlet } from 'react-router'
import { cva } from 'class-variance-authority'
import React, { useContext } from 'react'

const tabItemCva = cva(
  'px-2 after:content-[""] after:absolute after:left-1/2 after:-translate-x-1/2 after:w-2 after:h-1 after:rounded-lg hover:text-foreground rounded relative text-base',
  {
    variants: {
      active: {
        true: 'after:bg-accent text-foreground font-bold',
        false: 'text-muted-foreground-1 after:bg-transparent font-normal hover:bg-hover active:bg-active',
      },
      size: {
        large: 'h-10 leading-7 py-1.5 after:-bottom-3 ',
        middle: 'h-8 leading-6 py-1 after:-bottom-3 ',
        small: 'h-7 leading-5.5 py-0.75 after:-bottom-1.5 ',
      },
    },
    defaultVariants: {},
  },
)

type Context = {
  isActive: (to: To) => boolean
  size?: 'large' | 'middle' | 'small'
}

const NavTabContext = React.createContext<Context>({
  isActive: () => false,
  size: 'large',
})
const NavTabs: React.FC<
  React.ComponentProps<'div'> & {
    size?: 'large' | 'middle' | 'small'
  }
> = ({ className, size = 'large', children, ...rest }) => {
  const location = useLocation()
  const outlet = useOutlet()

  function getPath(p: string = '') {
    if (new RegExp('^/.*').test(p)) {
      return p
    } else {
      return outlet?.props.children.props.routeContext.matches.at(-2)?.pathname + (p ? '/' : '') + p
    }
  }
  const getTabPaths = (nodes: React.ReactNode): string[] => {
    return React.Children.toArray(nodes).flatMap((node) => {
      if (!React.isValidElement(node)) {
        return []
      }

      if (node.type === React.Fragment) {
        return getTabPaths(node.props.children)
      }

      const { to } = node.props as NavLinkProps
      if (!to) {
        return []
      }

      return [getPath(typeof to === 'string' ? to : to.pathname || '')]
    })
  }
  const values = getTabPaths(children)

  const isActive = (to: To) => {
    function getActiveTo(path: string) {
      if (values?.find((m) => m === path)) return path
      if (!path) return ''
      return getActiveTo(path.substring(0, path.lastIndexOf('/')))
    }
    if (typeof to === 'string') {
      return getPath(to) === getActiveTo(decodeURIComponent(location.pathname))
    } else {
      return getPath(to.pathname) === getActiveTo(decodeURIComponent(location.pathname))
    }
  }

  return (
    <div
      className={cn(
        'flex gap-4 py-3 px-4',
        {
          'py-1.5': size === 'small',
        },
        className,
      )}
      {...rest}
    >
      <NavTabContext.Provider value={{ isActive, size }}>{children}</NavTabContext.Provider>
    </div>
  )
}
const NavTabItem: React.FC<NavLinkProps> = ({ className, to, ...rest }) => {
  const { isActive, size } = useContext(NavTabContext)

  return (
    <NavLink
      className={(state) => {
        const customClassName = typeof className === 'string' ? className : className?.({ ...state, isActive: isActive(to) })
        return tabItemCva({ active: isActive(to), size, className: customClassName })
      }}
      to={to}
      {...rest}
    />
  )
}
export { NavTabs, NavTabItem }
